import React, { useState, useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";
import StripePayment from "../Stripe/StripePayment";
import { useTranslation } from "react-i18next"; // Add this import

const API_BASE = "https://xianhefairy.com/LotusFairy_API";

// Prices are shown and charged in USD only — StripePayment always creates USD payment intents
const currency = "USD";
const convert = (usd) => usd;
const format = (amount) =>
  `US$ ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

class PromoAlreadyRedeemedError extends Error {}

const PurchaseCoin = () => {
  const { t, i18n } = useTranslation();
  const [promotionCode, setPromotionCode] = useState("");
  const [promotionPercentage, setPromotionPercentage] = useState(0);
  const [reloadOnNotificationClose, setReloadOnNotificationClose] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoList, setPromoList] = useState([]);
  const [isPromoInputVisible, setIsPromoInputVisible] = useState(false);
  const [notification, setNotification] = useState("");
  const [notificationDetail, setNotificationDetail] = useState("");
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isTransactionSuccessful, setTransactionSuccessful] = useState(false);
  const [showGemBonusModal, setShowGemBonusModal] = useState(false);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const redeemInFlightRef = useRef(false);
  const purchaseInFlightRef = useRef(false);
  const processedPaymentsRef = useRef(new Set());
  // Selected packages: { [option uid]: quantity }
  const [cart, setCart] = useState({});

  // Add one of a package (tapping the same package again adds more)
  const handleAddOption = (option) => {
    setCart((prev) => ({ ...prev, [option.uid]: Math.min(99, (prev[option.uid] || 0) + 1) }));
  };

  const handleChangeQty = (uid, delta) => {
    setCart((prev) => {
      const next = { ...prev };
      const qty = Math.max(0, Math.min(99, (prev[uid] || 0) + delta));
      if (qty === 0) delete next[uid];
      else next[uid] = qty;
      return next;
    });
  };

  const clearCart = () => setCart({});

  useEffect(() => {
    setPromotionCode("");
    setPromotionPercentage(0);
  }, []);

  const userID = localStorage.getItem("userID");

  const fetchData = async () => {
    try {
      // Make the GET request
      const response = await fetch(
        `${API_BASE}/api/booking/getTotal?ID=${userID}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check if the response is OK
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      // Parse the JSON response
      const data = await response.json();
      localStorage.setItem("coins", data[0].COINS);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    window.location.reload();
  };

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/Booking/getPromo`);
        if (!response.ok) throw new Error("Failed to fetch promo codes");
        const data = await response.json();
        console.log("[Promo] getPromo data:", data);
        setPromoList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching promo codes:", error);
      }
    };
    fetchPromos();
  }, []);

  // EXPIRY_DATE comes back like "Sep 29 2027  3:59PM"
  const parsePromoExpiry = (expiry) => {
    if (!expiry) return null;
    const normalized = String(expiry).replace(/\s+/g, " ").replace(/(\d)(AM|PM)$/i, "$1 $2");
    const date = new Date(normalized);
    return isNaN(date.getTime()) ? null : date;
  };

  const isPromoExpired = (expiry) => {
    const date = parsePromoExpiry(expiry);
    return date !== null && date < new Date();
  };

  const formatPromoExpiry = (expiry) => {
    const date = parsePromoExpiry(expiry);
    if (!date) return "";
    const locale = i18n.language === "cn" ? "zh-CN" : i18n.language;
    return date.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
  };

  const promoValidUntilText = (promo) => {
    const date = formatPromoExpiry(promo.EXPIRY_DATE);
    return date ? t('purchaseCoin.promotionCodeValidUntil', { date }) : "";
  };

  const isPromoAlreadyRedeemed = async (promo) => {
    const custId = parseInt(localStorage.getItem("userID") || "0", 10);
    const response = await fetch(
      `${API_BASE}/api/Booking/getCustPromoRedeemed?CUST_ID=${custId}`
    );
    console.log("[Promo] getCustPromoRedeemed status:", response.status, "custId:", custId);
    if (!response.ok) throw new Error("Failed to fetch redeemed promos");
    const data = await response.json();
    console.log("[Promo] getCustPromoRedeemed data:", data);
    // Codes are single-use: once any member redeems it, nobody else can
    return (Array.isArray(data) ? data : []).some(
      (r) => (r.PROMO_CODE || "") === (promo.PROMO_CODE || "")
    );
  };

  // Ignore extra clicks while a redemption is in progress, otherwise a
  // double-click credits the gems twice
  const handleApplyPromotionCode = async () => {
    if (redeemInFlightRef.current) return;
    redeemInFlightRef.current = true;
    setIsRedeeming(true);
    try {
      // Keep the loading screen up for at least 1 second
      await Promise.all([
        redeemPromotionCode(),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
    } finally {
      redeemInFlightRef.current = false;
      setIsRedeeming(false);
    }
  };

  const redeemPromotionCode = async () => {
    const code = promotionCode.trim();
    const promo = promoList.find((p) => (p.PROMO_CODE || "") === code);
    console.log("[Promo] Apply pressed:", {
      entered: promotionCode,
      trimmed: code,
      promoList,
      matchedPromo: promo,
      expired: promo ? isPromoExpired(promo.EXPIRY_DATE) : null,
    });

    setNotificationDetail("");

    if (!promo) {
      console.log("[Promo] Invalid: code not found in promoList");
      setNotification(t('purchaseCoin.invalidPromotionCode'));
      setIsNotificationOpen(true);
      return;
    }

    if (isPromoExpired(promo.EXPIRY_DATE)) {
      console.log("[Promo] Invalid: code expired", promo.EXPIRY_DATE);
      setNotification(t('purchaseCoin.promotionCodeExpired'));
      setNotificationDetail(t('purchaseCoin.promotionCodeExpiredOn', { date: formatPromoExpiry(promo.EXPIRY_DATE) }));
      setIsNotificationOpen(true);
      return;
    }

    try {
      if (await isPromoAlreadyRedeemed(promo)) {
        setNotification(t('purchaseCoin.promotionCodeAlreadyRedeemed'));
        setIsNotificationOpen(true);
        return;
      }
    } catch (error) {
      console.error("Error checking redeemed promos:", error);
      setNotification(t('purchaseCoin.invalidPromotionCode'));
      setIsNotificationOpen(true);
      return;
    }

    const discount = Number(promo.PROMO_PERCENT) || 0;
    const bonusGems = Number(promo.GEM_PROMO) || 0;

    // Gem promos are credited to the account immediately on redeem
    if (bonusGems > 0) {
      try {
        await redeemPromoGems(promo, bonusGems);
        setNotification(t('purchaseCoin.promotionCodeGemsApplied', { gems: bonusGems }));
        setReloadOnNotificationClose(true);
        setPromotionCode("");
      } catch (error) {
        console.error("Error redeeming promo gems:", error);
        setNotification(
          error instanceof PromoAlreadyRedeemedError
            ? t('purchaseCoin.promotionCodeAlreadyRedeemed')
            : t('purchaseCoin.invalidPromotionCode')
        );
      }
      setIsNotificationOpen(true);
      setIsPromoInputVisible(false);
      return;
    }

    // Percentage promos are applied to the next purchase
    setAppliedPromo(promo);
    setPromotionPercentage(discount);
    setNotification(t('purchaseCoin.promotionCodeApplied', { discount }));
    setNotificationDetail(promoValidUntilText(promo));
    setIsNotificationOpen(true);
    setIsPromoInputVisible(false);
  };

  const fetchCurrentBalance = async () => {
    const res = await fetch(`${API_BASE}/api/booking/getTotal?ID=${userID}`);
    if (!res.ok) throw new Error("Failed to fetch gem balance");
    const data = await res.json();
    return Number(data?.[0]?.COINS) || 0;
  };

  const redeemPromoGems = async (promo, gems) => {
    let balanceBefore = null;
    try {
      balanceBefore = await fetchCurrentBalance();
    } catch (error) {
      console.error("[Promo] Could not read balance before redeem:", error);
    }

    // Claim the code first; API_PROMO_UPDATE returns no row if someone
    // already redeemed it, so gems are only added for the one winning claim
    const claimed = await claimCustomerPromo(promo);
    if (!claimed) throw new PromoAlreadyRedeemedError();

    let creditStatus = null;
    try {
      const response = await fetch(`${API_BASE}/api/Product/RedeemGem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          CustId: parseInt(userID || "0", 10),
          PromoCode: promo.PROMO_CODE,
        }),
      });
      creditStatus = response.status;
      console.log("[Promo] RedeemGem status:", response.status, await response.text());
    } catch (error) {
      console.error("[Promo] RedeemGem request failed:", error);
    }

    // The balance is the source of truth for whether the redeem worked
    let balanceAfter = null;
    try {
      balanceAfter = await fetchCurrentBalance();
      localStorage.setItem("coins", balanceAfter);
      setGemBalance(balanceAfter);
    } catch (error) {
      console.error("[Promo] Could not read balance after redeem:", error);
    }

    const credited =
      balanceBefore !== null && balanceAfter !== null
        ? balanceAfter >= balanceBefore + gems
        : creditStatus !== null && creditStatus >= 200 && creditStatus < 300;
    console.log("[Promo] Redeem result:", { balanceBefore, balanceAfter, gems, creditStatus, credited });
    if (!credited) throw new Error("Failed to credit promo gems");
  };

  // Returns true if this member now owns the code, false if it was already redeemed
  const claimCustomerPromo = async (promo) => {
    const custId = parseInt(localStorage.getItem("userID") || "0", 10);
    const response = await fetch(`${API_BASE}/api/Booking/AddCustomerPromo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        CUST_ID: custId,
        PROMO_CODE: promo.PROMO_CODE,
        PROMO_ID: promo.UID ?? promo.PROMO_ID ?? 0,
        CREATED_BY: custId,
      }),
    });
    if (!response.ok) throw new Error("Failed to record customer promo");
    const data = await response.json();
    console.log("[Promo] AddCustomerPromo data:", data);
    if (Array.isArray(data)) return data.length > 0;
    return Boolean(data);
  };

  const recordCustomerPromo = async (promo) => {
    if (!promo) return;
    try {
      await claimCustomerPromo(promo);
    } catch (error) {
      console.error("Error recording customer promo:", error);
    }
  };

  // Returns true if the purchase was sent, false if it was skipped as a duplicate.
  // `dryRun` skips the API call and all side effects (used by the duplicate checker).
  const handlePurchase = async (paymentIntentId, { dryRun = false } = {}) => {
    // Record each Stripe payment only once, even if success is reported twice
    if (purchaseInFlightRef.current) {
      console.warn("PostPurchaseCoin already in progress, skipping duplicate call");
      return false;
    }
    if (paymentIntentId && processedPaymentsRef.current.has(paymentIntentId)) {
      console.warn("PostPurchaseCoin already sent for payment:", paymentIntentId);
      return false;
    }
    purchaseInFlightRef.current = true;
    if (paymentIntentId) processedPaymentsRef.current.add(paymentIntentId);

    const userID = localStorage.getItem("userID");

    // One line item per selected package
    const purchasePost = {
      prefix: "PCN",
      PaymentIntentId: paymentIntentId || null, // Stripe PaymentIntent id, lets the backend reject duplicates
      items: cartLines.map((line) => ({
        CustomerId: userID,
        pid: Number(line.uid), // gem option ID from GetGemOptions
        Quantity: line.qty, // number of packages selected
        Amount: line.lineAmount,
      })),
    };

    console.log(dryRun ? "[DRY RUN] PostPurchaseCoin body (not sent):" : "PostPurchaseCoin body:", purchasePost);

    if (dryRun) {
      // Stand-in for the network call so the in-flight guard is exercised
      await new Promise((resolve) => setTimeout(resolve, 300));
      purchaseInFlightRef.current = false;
      return true;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/member/PostPurchaseCoin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(purchasePost),
        }
      );

      const data = await response.json();
      console.log("PostPurchaseCoin result:", response.status, data);

      if (data) {
        console.log(`Purchase successful`);
        await recordCustomerPromo(appliedPromo);
        fetchData();
      } else {
        console.log("Did not work");
      }
    } catch (error) {
      console.log(`Error: ${error.message}`);
    } finally {
      purchaseInFlightRef.current = false;
    }

    // Retrieve the current coin balance from localStorage
    const currentCoins = parseInt(localStorage.getItem("coins")) || 0;

    // Update local coins with the purchased gems
    const updatedCoins = currentCoins + totalGems;

    localStorage.setItem("coins", updatedCoins);

    setTransactionSuccessful(true);
    return true;
  };

  // Duplicate-purchase checker, safe to run on live: dry run only, no API call,
  // no gems credited. Run `__checkPurchaseDuplicates()` in the browser console
  // while on this page.
  const handlePurchaseRef = useRef(handlePurchase);
  handlePurchaseRef.current = handlePurchase;

  useEffect(() => {
    window.__checkPurchaseDuplicates = async () => {
      const purchase = (id) => handlePurchaseRef.current(id, { dryRun: true });
      const stamp = Date.now();
      const results = [];
      const check = (scenario, sent, expected) =>
        results.push({ scenario, sent, expected, result: sent === expected ? "PASS" : "FAIL" });

      // 1. Success reported twice at the same time (double click / Stripe re-confirm)
      const idA = `pi_check_${stamp}_a`;
      const concurrent = await Promise.all([purchase(idA), purchase(idA)]);
      check("Same payment, reported twice at once", concurrent.filter(Boolean).length, 1);

      // 2. Same payment reported again after the first finished (3DS redirect re-run)
      const again = await purchase(idA);
      check("Same payment, reported again later", again ? 1 : 0, 0);

      // 3. A genuinely new payment must still go through
      const idB = `pi_check_${stamp}_b`;
      const fresh = await purchase(idB);
      check("Different payment", fresh ? 1 : 0, 1);

      console.table(results);
      const ok = results.every((r) => r.result === "PASS");
      console.log(ok ? "✅ Duplicate guard OK" : "❌ Duplicate guard FAILED");
      return ok;
    };
    return () => {
      delete window.__checkPurchaseDuplicates;
    };
  }, []);

  const handleTogglePromoInput = () => {
    setIsPromoInputVisible(!isPromoInputVisible);
  };

  const closeNotification = () => {
    setIsNotificationOpen(false);
    if (reloadOnNotificationClose) window.location.reload();
  };

  const closeTransactionModal = () => {
    setTransactionSuccessful(false);
  };

  //Stripe
  const [modalOpen, setModalOpen] = useState(false);
  const [guardianModal, setGuardianModal] = useState(false);
  const userAgeRef = useRef(undefined);
  // null until the profile loads, so the Ambassador section doesn't flash for Ambassadors/ICs
  const [isAmbassador, setIsAmbassador] = useState(null);
  const [gemBalance, setGemBalance] = useState(() => Number(localStorage.getItem("coins")) || 0);

  const fetchUserAge = async () => {
    if (userAgeRef.current !== undefined) return userAgeRef.current;
    try {
      const res = await fetch(`${API_BASE}/api/User/GetProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          UserName: localStorage.getItem("username") || "",
          ID: parseInt(userID || "0", 10),
          LangId: parseInt(localStorage.getItem("languageId") || "2224", 10) || 2224,
          UserToken: "",
          Token: { AccessToken: "xAfhn90xflfHfd.fjldfj@fdhLXHCHchlhsdflsdfhawWFRwzxxp" },
        }),
      });
      if (!res.ok) { userAgeRef.current = null; return null; }
      const data = await res.json();
      const profile = data?.Profiles?.[0];
      // Ambassadors have an AP code (VP_CODE) and ICs an IC code (VVP_CODE); everyone else is a Free Explorer
      setIsAmbassador(Boolean(profile?.VP_CODE || profile?.VVP_CODE));
      const age = profile?.Age ?? null;
      userAgeRef.current = age;
      return age;
    } catch {
      userAgeRef.current = null;
      return null;
    }
  };

  useEffect(() => {
    fetchUserAge();

    const fetchGemBalance = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/booking/getTotal?ID=${userID}`);
        if (!response.ok) throw new Error("Failed to fetch gem balance");
        const data = await response.json();
        const coins = Number(data?.[0]?.COINS) || 0;
        localStorage.setItem("coins", coins);
        setGemBalance(coins);
      } catch (error) {
        console.error("Error fetching gem balance:", error);
      }
    };
    fetchGemBalance();
  }, []);

  const openModal = async () => {
    if (!totalAmount) return;
    const age = await fetchUserAge();
    if (age !== null && age < 18) {
      setGuardianModal(true);
    } else {
      setModalOpen(true);
    }
  };

  // Determine a selected plan — for now we map coin purchases to a default plan id
  // Assumption: the app doesn't use different named plans here; use a simple fallback
  const selectedPlan = "coins";

  // Wrapper for payment success to reuse existing purchase logic
  const handlePaymentSuccess = async (paymentIntent) => {
    // Call the existing purchase handler
    await handlePurchase(paymentIntent?.id);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    // Close modal if the user clicks outside
    const handleOutsideClick = (e) => {
      const modal = document.getElementById("stripeModal"); // Target the modal element
      if (modal && !modal.contains(e.target)) {
        setModalOpen(false); // Close modal if clicked outside
      }
    };

    if (modalOpen) {
      document.addEventListener("mousedown", handleOutsideClick); // Add event listener
    }

    // Cleanup the event listener when modal is closed
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [modalOpen]); // Only run when modalOpen changes

  const [purchaseOptions, setPurchaseOptions] = useState([]);

  useEffect(() => {
    const fetchGemOptions = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/Product/GetGemOptions?CustId=${userID}`);
        const data = await response.json();
        // Map API data to your button format
        const options = data.map(opt => ({
          usd: Number(opt.Amount),
          gems: Number(opt.Gem),
          label: `US$ ${opt.Amount} - ${opt.Gem} Gems`,
          uid: opt.Uid,
        }));
        setPurchaseOptions(options);
      } catch (error) {
        console.error("Failed to fetch gem options:", error);
      }
    };
    fetchGemOptions();
  }, []);

  // Selected packages with their per-line totals (promotion applied).
  // `lineAmount` is the USD amount recorded with the purchase; `price`/`linePrice`
  // and the totals below are what Stripe charges.
  const discountFactor = 1 - promotionPercentage / 100;
  const cartLines = purchaseOptions
    .filter((opt) => cart[opt.uid] > 0)
    .map((opt) => {
      const qty = cart[opt.uid];
      const price = convert(opt.usd);
      return {
        ...opt,
        qty,
        price,
        lineGems: Math.round(opt.gems * discountFactor) * qty,
        lineAmount: Number((opt.usd * qty * discountFactor).toFixed(2)),
        linePrice: Number((price * qty * discountFactor).toFixed(2)),
      };
    });
  const totalGems = cartLines.reduce((sum, line) => sum + line.lineGems, 0);
  const totalAmount = Number(cartLines.reduce((sum, line) => sum + line.linePrice, 0).toFixed(2));
  const totalPackages = cartLines.reduce((sum, line) => sum + line.qty, 0);

  const subtotal = Number(cartLines.reduce((sum, line) => sum + line.price * line.qty, 0).toFixed(2));
  const discountAmount = Number((subtotal - totalAmount).toFixed(2));
  const cartPanelRef = useRef(null);

  // Cart page styles (injected so media queries can be used)
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      @keyframes redeem-spin { to { transform: rotate(360deg); } }

      .pc-layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 340px;
        gap: 24px;
        max-width: 1100px;
        margin: 0 auto;
        text-align: left;
        align-items: start;
      }
      .pc-main {
        padding-right: 24px;
        border-right: 1px solid rgba(223, 189, 105, 0.35);
      }
      .pc-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
        gap: 12px;
      }
      .pc-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 14px 16px;
        border: 1px solid rgba(223, 189, 105, 0.45);
        border-radius: 10px;
        background: #0d0d0d;
        color: #DFBD69;
        transition: border-color 0.2s, background 0.2s;
      }
      .pc-item.in-cart {
        border-color: #DFBD69;
        background: rgba(223, 189, 105, 0.08);
        box-shadow: inset 0 0 0 1px #DFBD69;
      }
      .pc-item-gems { font-size: 18px; font-weight: bold; }
      .pc-item-price { font-size: 14px; opacity: 0.85; margin-top: 2px; }
      .pc-add-btn {
        background: #DFBD69;
        color: #151517;
        border: none;
        border-radius: 6px;
        padding: 8px 16px;
        font-weight: bold;
        font-size: 14px;
        cursor: pointer;
        white-space: nowrap;
      }
      .pc-stepper { display: inline-flex; align-items: center; gap: 8px; }
      .pc-stepper-qty { min-width: 20px; text-align: center; font-weight: bold; }
      .pc-step-btn {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: 1px solid #DFBD69;
        background: transparent;
        color: #DFBD69;
        font-size: 16px;
        line-height: 1;
        padding: 0;
        cursor: pointer;
      }
      .pc-step-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .pc-side { position: sticky; top: 0; }
      .pc-balance {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 10px;
        margin-bottom: 12px;
        padding: 12px 18px;
        border: 1px solid rgba(223, 189, 105, 0.35);
        border-radius: 12px;
        color: #DFBD69;
        font-size: 14px;
      }
      .pc-balance-value { font-size: 18px; font-weight: bold; }
      .pc-cart {
        background: #111;
        border: 1px solid rgba(223, 189, 105, 0.35);
        border-radius: 12px;
        padding: 18px;
        color: #DFBD69;
      }
      .pc-cart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
      }
      .pc-cart-title { font-size: 18px; font-weight: bold; }
      .pc-link-btn {
        background: transparent;
        border: none;
        color: #DFBD69;
        text-decoration: underline;
        font-size: 13px;
        cursor: pointer;
        padding: 0;
      }
      .pc-cart-lines { max-height: 280px; overflow-y: auto; }
      .pc-cart-line {
        display: grid;
        grid-template-columns: 1fr auto;
        align-items: center;
        gap: 8px 10px;
        padding: 10px 0;
        border-bottom: 1px solid rgba(223, 189, 105, 0.2);
      }
      .pc-cart-line-name { font-weight: bold; }
      .pc-cart-line-sub { font-size: 13px; opacity: 0.8; }
      .pc-cart-line-total { font-weight: bold; text-align: right; }
      .pc-cart-empty { padding: 18px 0; opacity: 0.8; font-size: 14px; text-align: center; }
      .pc-row { display: flex; justify-content: space-between; font-size: 14px; margin: 6px 0; }
      .pc-total-row { font-size: 18px; font-weight: bold; }
      .pc-redeem {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        margin-top: 16px;
      }
      .pc-redeem-link { font-size: 15px; align-self: center; }
      .pc-redeem input {
        width: 100%;
        box-sizing: border-box;
        padding: 10px 12px;
        border-radius: 6px;
        border: 1px solid rgba(223, 189, 105, 0.5);
        background: #000;
        color: #DFBD69;
        font-size: 16px;
      }
      .pc-redeem-btn {
        background: #DFBD69;
        color: #151517;
        border: none;
        border-radius: 6px;
        padding: 10px 20px;
        font-size: 16px;
        cursor: pointer;
      }
      .pc-redeem-btn:disabled { opacity: 0.6; cursor: wait; }
      .pc-checkout {
        width: 100%;
        margin-top: 14px;
        background: #DFBD69;
        color: #151517;
        border: none;
        border-radius: 8px;
        padding: 12px;
        font-weight: bold;
        font-size: 16px;
        cursor: pointer;
      }
      .pc-checkout:disabled { opacity: 0.5; cursor: not-allowed; }
      .pc-mobile-bar { display: none; }
      @media (max-width: 900px) {
        .pc-layout { grid-template-columns: 1fr; padding: 0 12px; }
        .pc-main {
          padding-right: 0;
          border-right: none;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(223, 189, 105, 0.35);
        }
        .pc-side { position: static; }
        .pc-mobile-bar {
          display: flex;
          position: sticky;
          bottom: 0;
          z-index: 5;
          margin-top: 16px;
          padding: 12px 16px;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          background: #111;
          border: 1px solid rgba(223, 189, 105, 0.45);
          border-radius: 10px;
          box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.6);
          color: #DFBD69;
          text-align: left;
        }
        .pc-mobile-bar .pc-checkout { width: auto; margin-top: 0; padding: 10px 18px; }
      }
      @media (max-width: 480px) {
        .pc-list { grid-template-columns: 1fr; }
      }
      @media (min-width: 769px) and (max-width: 1024px) {
        .page-container { padding: 20px 0 !important; }
        .page-content { padding: 20px 0 !important; }
      }
    `;
    document.head.appendChild(styleTag);
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  const renderStepper = (uid, qty) => (
    <div className="pc-stepper">
      <button className="pc-step-btn" onClick={() => handleChangeQty(uid, -1)} aria-label="Decrease quantity">
        −
      </button>
      <span className="pc-stepper-qty">{qty}</span>
      <button className="pc-step-btn" onClick={() => handleChangeQty(uid, 1)} disabled={qty >= 99} aria-label="Increase quantity">
        +
      </button>
    </div>
  );

  return (
    <div className="page-container" style={pageContainerStyle}>
      <div className="page-content" style={pageContentStyle}>
        <h2 style={{ color: "#DFBD69" }}>{t('purchaseCoin.title')}</h2>

        <div className="pc-layout">
          {/* Packages */}
          <div className="pc-main">
            <div style={{ marginBottom: "12px", color: "#DFBD69", fontSize: "16px" }}>
              {t('purchaseCoin.selectOption')}
            </div>
            <div className="pc-list">
              {purchaseOptions.map((option) => {
                const qty = cart[option.uid] || 0;
                return (
                  <div key={option.uid} className={`pc-item ${qty ? "in-cart" : ""}`}>
                    <div>
                      <div className="pc-item-gems">
                        {option.gems.toLocaleString()} {t('purchaseCoin.gems')}
                      </div>
                      <div className="pc-item-price">{format(convert(option.usd))}</div>
                    </div>
                    {qty ? (
                      renderStepper(option.uid, qty)
                    ) : (
                      <button className="pc-add-btn" onClick={() => handleAddOption(option)}>
                        {t('purchaseCoin.addToCart')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{ color: "#DFBD69", margin: "14px 0", fontSize: "14px", opacity: 0.8 }}>
              {t('purchaseCoin.conversionRate')}
              {currency !== "USD" && ` · US$1 = ${format(convert(1))}`}
            </div>

            {isAmbassador === false && (
            <div style={{ margin: "20px 0", textAlign: "center" }}>
              <div style={{ color: "#DFBD69", fontSize: "18px", marginBottom: "14px" }}>
                {t('purchaseCoin.ambassadorBonusInfo')}
              </div>
              <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                <button
                  onClick={() => setShowGemBonusModal(true)}
                  style={{
                    background: "transparent",
                    color: "#DFBD69",
                    border: "1px solid #DFBD69",
                    borderRadius: "6px",
                    padding: "8px 18px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: "15px",
                  }}
                >
                  {t('purchaseCoin.compareGemBonuses')}
                </button>
                <a
                  href="/affiliatepartner"
                  style={{
                    background: "#DFBD69",
                    color: "#151517",
                    border: "1px solid #DFBD69",
                    borderRadius: "6px",
                    padding: "8px 18px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    fontSize: "15px",
                    textDecoration: "none",
                    display: "inline-block",
                  }}
                >
                  {t('purchaseCoin.signUpAsAmbassador')}
                </a>
              </div>
            </div>
            )}
          </div>

          {/* Right column: balance, cart, redemption code */}
          <div className="pc-side">
          <div className="pc-balance">
            <span>{t('purchaseCoin.currentBalance')}</span>
            <span className="pc-balance-value">
              {gemBalance.toLocaleString()} {t('purchaseCoin.gems')}
            </span>
          </div>

          {/* Cart */}
          <div className="pc-cart" ref={cartPanelRef}>
            <div className="pc-cart-header">
              <span className="pc-cart-title">
                {t('purchaseCoin.yourCart')} ({totalPackages})
              </span>
              {cartLines.length > 0 && (
                <button className="pc-link-btn" onClick={clearCart}>
                  {t('purchaseCoin.clear')}
                </button>
              )}
            </div>

            {cartLines.length === 0 ? (
              <div className="pc-cart-empty">{t('purchaseCoin.noPackagesSelected')}</div>
            ) : (
              <div className="pc-cart-lines">
                {cartLines.map((line) => (
                  <div key={line.uid} className="pc-cart-line">
                    <div>
                      <div className="pc-cart-line-name">
                        {line.gems.toLocaleString()} {t('purchaseCoin.gems')}
                      </div>
                      <div className="pc-cart-line-sub">
                        {format(line.price)} {t('purchaseCoin.each')}
                      </div>
                    </div>
                    <div className="pc-cart-line-total">{format(line.price * line.qty)}</div>
                    {renderStepper(line.uid, line.qty)}
                    <button className="pc-link-btn" style={{ justifySelf: "end" }} onClick={() => handleChangeQty(line.uid, -line.qty)}>
                      {t('purchaseCoin.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div style={{ borderTop: "1px solid rgba(223,189,105,0.35)", marginTop: "10px", paddingTop: "8px" }}>
              <div className="pc-row">
                <span>{t('purchaseCoin.subtotal')}</span>
                <span>{format(subtotal)}</span>
              </div>
              {promotionPercentage > 0 && (
                <div className="pc-row">
                  <span>{t('purchaseCoin.discount')} (-{promotionPercentage}%)</span>
                  <span>-{format(discountAmount)}</span>
                </div>
              )}
              <div className="pc-row pc-total-row">
                <span>{t('purchaseCoin.totalGems')}</span>
                <span>{totalGems.toLocaleString()}</span>
              </div>
              <div className="pc-row pc-total-row">
                <span>{t('purchaseCoin.totalAmount')}</span>
                <span>{format(totalAmount)}</span>
              </div>
            </div>

            <button className="pc-checkout" onClick={openModal} disabled={!totalAmount}>
              {t('purchaseCoin.checkout')}
            </button>
          </div>

          {/* Redemption code */}
          {!promotionPercentage && (
            <div className="pc-redeem">
              {isPromoInputVisible ? (
                <>
                  <input
                    type="text"
                    value={promotionCode}
                    onChange={(e) => setPromotionCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleApplyPromotionCode(); }}
                    placeholder={t('purchaseCoin.promotionCodePlaceholder')}
                    autoFocus
                  />
                  <button className="pc-redeem-btn" onClick={handleApplyPromotionCode} disabled={isRedeeming}>
                    {t('purchaseCoin.applyPromotionCode')}
                  </button>
                </>
              ) : (
                <button className="pc-link-btn pc-redeem-link" onClick={handleTogglePromoInput}>
                  {t('purchaseCoin.haveRedemptionCode')}
                </button>
              )}
            </div>
          )}
          </div>
        </div>

        {/* Mobile: totals stay pinned at the bottom while scrolling the packages */}
        {cartLines.length > 0 && (
          <div className="pc-mobile-bar">
            <div
              style={{ cursor: "pointer" }}
              onClick={() => cartPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <div style={{ fontSize: "13px", opacity: 0.85, textDecoration: "underline" }}>
                {t('purchaseCoin.viewCart')} ({totalPackages})
              </div>
              <div style={{ fontWeight: "bold" }}>
                {totalGems.toLocaleString()} {t('purchaseCoin.gems')} · {format(totalAmount)}
              </div>
            </div>
            <button className="pc-checkout" onClick={openModal} disabled={!totalAmount}>
              {t('purchaseCoin.checkout')}
            </button>
          </div>
        )}
      </div>

      {/* Redeeming loading screen */}
      {isRedeeming && (
        <div style={redeemLoadingOverlayStyle}>
          <div style={redeemSpinnerStyle} />
          <p style={{ color: "#DFBD69", marginTop: "16px", fontSize: "16px" }}>
            {t('purchaseCoin.redeemingCode')}
          </p>
        </div>
      )}

      {/* Notification Modal */}
      {isNotificationOpen && (
        <div style={notificationOverlayStyle} onClick={closeNotification}>
          <div style={notificationModalStyle}>
            <p>{notification}</p>
            {notificationDetail && (
              <p style={{ marginTop: "8px", fontSize: "14px", color: "#555" }}>{notificationDetail}</p>
            )}
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      <div>
        {modalOpen && (
          (() => {
            const stripeAmount = totalAmount;
            const stripeGems = totalGems;

            console.log("Sending to Stripe:", {
              id: selectedPlan,
              amount: stripeAmount,
              gems: stripeGems,
              currency,
              isOpen: modalOpen,
            });

            return (
              <div style={styles.modalOverlay} onClick={closeModal}>
                <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                  <StripePayment
                    id={selectedPlan}
                    amount={stripeAmount}
                    gems={stripeGems}
                    currency={currency.toLowerCase()}
                    isOpen={modalOpen}
                    onClose={closeModal}
                    onPaymentSuccess={handlePaymentSuccess}
                  />
                </div>
              </div>
            );
          })()
        )}
      </div>

      {/* Guardian Confirmation Modal */}
      {guardianModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10002 }}
          onClick={() => setGuardianModal(false)}>
          <div style={{ backgroundColor: "#1a1a1a", borderRadius: 12, padding: "28px 24px", width: "min(460px, 92vw)", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.45)", border: "1px solid rgba(223,189,105,0.25)" }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: "#DFBD69", margin: "0 0 16px", fontSize: "1.2rem" }}>{t("cart.guardianConfirmationRequired")}</h3>
            <p style={{ color: "#fff2d2", marginBottom: 28, lineHeight: 1.6 }}>{t("cart.guardianMessage")}</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button style={{ background: "#DFBD69", color: "#151517", border: "none", borderRadius: 8, padding: "10px 22px", fontWeight: 700, cursor: "pointer", fontSize: "0.95rem" }}
                onClick={() => { setGuardianModal(false); setModalOpen(true); }}>
                {t("cart.yesProceed")}
              </button>
              <button style={{ background: "transparent", color: "#DFBD69", border: "1px solid rgba(223,189,105,0.4)", borderRadius: 8, padding: "10px 22px", cursor: "pointer", fontSize: "0.95rem" }}
                onClick={() => setGuardianModal(false)}>
                {t("cart.noCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gem Bonus Comparison Modal */}
      {showGemBonusModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10002 }}
          onClick={() => setShowGemBonusModal(false)}>
          <div style={{ backgroundColor: "#1a1a1a", borderRadius: 12, padding: "28px 24px", width: "min(640px, 94vw)", boxShadow: "0 8px 32px rgba(0,0,0,0.45)", border: "1px solid rgba(223,189,105,0.25)", position: "relative" }}
            onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowGemBonusModal(false)}
              style={{ position: "absolute", top: 16, right: 16, background: "transparent", border: "none", color: "#DFBD69", cursor: "pointer", fontSize: "1.1rem" }}
            >
              <FaTimes />
            </button>
            <h3 style={{ color: "#DFBD69", margin: "0 0 20px", fontSize: "1.2rem", textAlign: "center" }}>
              {t('purchaseCoin.gemBonusTable.title')}
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "560px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#c9c9c9" }}>
                    {[
                      t('purchaseCoin.gemBonusTable.priceUsd'),
                      t('purchaseCoin.gemBonusTable.explorer'),
                      t('purchaseCoin.gemBonusTable.ambassador'),
                      t('purchaseCoin.gemBonusTable.plusAmount'),
                      t('purchaseCoin.gemBonusTable.plusPercent'),
                    ].map((heading, idx) => (
                      <th key={idx} style={{ padding: "10px 12px", color: "#151517", fontWeight: 700, fontSize: "0.9rem", textAlign: "center", border: "1px solid #444" }}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {gemBonusTableRows.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: "8px 12px", color: "#DFBD69", textAlign: "center", border: "1px solid #444" }}>{row.price}</td>
                      <td style={{ padding: "8px 12px", color: "#fff2d2", textAlign: "center", border: "1px solid #444" }}>{row.explorer}</td>
                      <td style={{ padding: "8px 12px", color: "#fff2d2", textAlign: "center", border: "1px solid #444" }}>{row.ambassador}</td>
                      <td style={{ padding: "8px 12px", color: "#fff2d2", textAlign: "center", border: "1px solid #444" }}>{row.plusAmount}</td>
                      <td style={{ padding: "8px 12px", color: "#fff2d2", textAlign: "center", border: "1px solid #444" }}>{row.plusPercent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const gemBonusTableRows = [
  { price: "$0.99", explorer: "10 Gem", ambassador: "10 Gem", plusAmount: "--", plusPercent: "0%" },
  { price: "$4.99", explorer: "50 Gem", ambassador: "52 Gem", plusAmount: "+2", plusPercent: "+4%" },
  { price: "$9.99", explorer: "100 Gem", ambassador: "105 Gem", plusAmount: "+5", plusPercent: "+5%" },
  { price: "$49.99", explorer: "500 Gem", ambassador: "530 Gem", plusAmount: "+30", plusPercent: "+6%" },
  { price: "$99.99", explorer: "1,000 Gem", ambassador: "1,070 Gem", plusAmount: "+70", plusPercent: "+7%" },
  { price: "$199.99", explorer: "2,000 Gem", ambassador: "2,150 Gem", plusAmount: "+150", plusPercent: "+7.5%" },
  { price: "$499.99", explorer: "5,000 Gem", ambassador: "5,400 Gem", plusAmount: "+400", plusPercent: "+8%" },
  { price: "$999.00", explorer: "10,000 Gem", ambassador: "10,850 Gem", plusAmount: "+850", plusPercent: "+8.5%" },
  { price: "$4,999.00", explorer: "50,000 Gem", ambassador: "54,500 Gem", plusAmount: "+4,500", plusPercent: "+9%" },
  { price: "$9,999.00", explorer: "100,000 Gem", ambassador: "110,000 Gem", plusAmount: "+10,000", plusPercent: "+10%" },
];

// Page styles
const pageContainerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start", // Changed from "center"
  minHeight: "100vh", // Changed from height: "80vh"
  backgroundColor: "black",
  padding: "20px 0", // Add vertical padding
};

const pageContentStyle = {
  width: "100%",
  maxWidth: "100%",
  backgroundColor: "black",
  padding: "30px",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  textAlign: "center",
  margin: "0 auto",
  minHeight: "auto",
  maxHeight: "80vh",         // <-- Add this line
  overflowY: "auto",         // <-- Add this line
};

const notificationOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1001,
};

const notificationModalStyle = {
  backgroundColor: "white",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  textAlign: "center",
};

const redeemLoadingOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10003,
};

const redeemSpinnerStyle = {
  width: "48px",
  height: "48px",
  border: "4px solid rgba(223, 189, 105, 0.25)",
  borderTopColor: "#DFBD69",
  borderRadius: "50%",
  animation: "redeem-spin 0.8s linear infinite",
};

// Styles used by the Stripe modal overlay/content
const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "20px",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "90%",
    width: "420px",
  },
};

export default PurchaseCoin;
